import { apiClient } from "./client";

export type LogbookKind =
  | "REGULATION_CALL"
  | "SMUR_DISPATCH"
  | "TRANSFER"
  | "TUTORING"
  | "CONFERENCE"
  | "FORMATION"
  | "DPC_ACTION"
  | "OTHER";

export type LogbookComplexity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type LogbookEntry = {
  id: string;
  practitioner_id: string;
  assignment_id: string | null;
  date: string;
  kind: LogbookKind;
  label: string;
  count: number;
  duration_min: number | null;
  complexity: LogbookComplexity | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
};

export type LogbookStats = {
  period_from: string;
  period_to: string;
  total_entries: number;
  total_count: number;
  by_kind: Record<string, number>;
  by_complexity: Record<string, number>;
};

export type ListLogbookParams = {
  from?: string;
  to?: string;
  kind?: LogbookKind;
  q?: string;
};

export async function listLogbook(params: ListLogbookParams = {}): Promise<LogbookEntry[]> {
  const { data } = await apiClient.get<LogbookEntry[]>("/api/v1/me/logbook", { params });
  return data;
}

export type CreateLogbookEntryPayload = {
  date: string;
  kind: LogbookKind;
  label: string;
  count?: number;
  duration_min?: number | null;
  complexity?: LogbookComplexity | null;
  comment?: string | null;
  assignment_id?: string | null;
};

export async function createLogbookEntry(
  p: CreateLogbookEntryPayload,
): Promise<LogbookEntry> {
  const { data } = await apiClient.post<LogbookEntry>("/api/v1/me/logbook", p);
  return data;
}

export async function updateLogbookEntry(
  id: string,
  p: Partial<CreateLogbookEntryPayload>,
): Promise<LogbookEntry> {
  const { data } = await apiClient.patch<LogbookEntry>(`/api/v1/me/logbook/${id}`, p);
  return data;
}

export async function deleteLogbookEntry(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/me/logbook/${id}`);
}

export async function getLogbookStats(from: string, to: string): Promise<LogbookStats> {
  const { data } = await apiClient.get<LogbookStats>("/api/v1/me/logbook/stats", {
    params: { from, to },
  });
  return data;
}

export type ImportResult = { imported: number; skipped: number };

export async function importLogbookFromAssignments(
  from_date: string,
  to_date: string,
  kind: LogbookKind = "REGULATION_CALL",
): Promise<ImportResult> {
  const { data } = await apiClient.post<ImportResult>(
    "/api/v1/me/logbook/import-from-assignments",
    { from_date, to_date, kind },
  );
  return data;
}

export async function exportLogbookPdf(
  from: string,
  to: string,
): Promise<{ blob: Blob; filename: string }> {
  const resp = await apiClient.get<Blob>("/api/v1/me/logbook/report.pdf", {
    params: { from, to },
    responseType: "blob",
  });
  const dispo = (resp.headers["content-disposition"] as string | undefined) ?? "";
  const match = /filename="([^"]+)"/.exec(dispo);
  const filename = match?.[1] ?? `cortex-carnet_${from}_${to}.pdf`;
  return { blob: resp.data, filename };
}
